import type { Integrador, Projeto } from '@/dados';

/* etapas.ts — FONTE ÚNICA do funil.
 *
 * A ordem do array É a ordem do fluxo. Para mudar o funil (acrescentar,
 * remover ou reordenar etapa) edita-se SÓ esta lista: o stepper, a navegação
 * lateral, o rodapé de cada etapa e o cálculo de progresso são derivados
 * daqui. Não existe marcação de passo duplicada em tela nenhuma — foi assim
 * que a versão anterior acumulou steppers divergentes.
 *
 * ── POR QUE O PROGRESSO MORA AQUI, E NÃO NA TELA ───────────────────────────
 * Antes, "etapa concluída" era `índice < índice atual`: a trilha dizia que as
 * quatro primeiras estavam prontas só porque a pessoa tinha chegado na quinta.
 * Numa ferramenta em que se volta para corrigir custo o tempo todo, isso é
 * falso na primeira vez que alguém usa o menu, e um indicador de progresso que
 * mente é pior que nenhum: some com a única pergunta que o usuário tem no meio
 * do funil, que é "o que ainda falta de mim".
 *
 * `preenchida` responde essa pergunta a partir do DADO, não da navegação. Cada
 * etapa declara o mínimo que precisa ter sido informado para valer como
 * resolvida — deliberadamente o MÍNIMO, e não "todo campo cheio": campo
 * opcional que reprova a etapa transforma o funil num formulário obrigatório
 * disfarçado.
 *
 * Isto NÃO é regra de precificação e por isso não desce para o `core`: o motor
 * não tem opinião sobre etapa de tela. O que ele tem, e o que estas funções
 * respeitam, é a premissa de que custo sem configuração vale zero — por isso
 * "preenchida" nunca inventa valor, só constata presença. */

/** O que a trilha precisa saber do banco para dizer o que já está resolvido. */
export type DadosDoFunil = {
  integrador: Integrador | null | undefined;
  projeto: Projeto | null | undefined;
};

export type Etapa = {
  /** Identificador da etapa; a tela usa para se localizar no funil. */
  id: string;
  /** Rótulo CURTO: é o que aparece na trilha, ao lado do hexágono. */
  rotulo: string;
  /** Nome por extenso: título da etapa e item da navegação lateral. */
  nome: string;
  /** Rota do React Router. */
  rota: string;
  /** Uma linha explicando o que a etapa decide. Vira legenda/tooltip. */
  resumo: string;
  /** Bloco da navegação lateral em que a etapa aparece. */
  grupo: 'configuracao' | 'precificacao';
  /**
   * O casco da tela.
   *
   * `formulario` divide a tela em duas: a marca à esquerda e a etapa numa
   * coluna com medida própria. `painel` usa a largura inteira, que é o que
   * tabela, cascata e DRE pedem.
   *
   * Mora aqui, e não numa prop escrita em cada tela, pela mesma razão que a
   * ordem e o rótulo moram aqui: etapa nova entra editando esta lista. Uma
   * tela que declara o próprio casco é uma tela que vai discordar das outras
   * seis no dia em que alguém acrescentar a sétima.
   */
  casco: 'formulario' | 'painel';
  /**
   * Etapas que precisam estar resolvidas antes desta abrir. Vazio = sempre
   * aberta. É a ÚNICA trava de navegação do funil, e é curta de propósito:
   * as etapas de configuração são independentes entre si e travar a ordem
   * delas só obrigaria a pessoa a passar por telas que ela não veio resolver.
   */
  requer: readonly string[];
  /** O mínimo que precisa existir no banco para a etapa valer como resolvida. */
  preenchida: (dados: DadosDoFunil) => boolean;
};

/* ── Leitura tolerante da linha crua ────────────────────────────────────────
 * Os campos vêm de colunas jsonb escritas por versões diferentes do app: um
 * número pode chegar como string, e "vazio" pode ser null, undefined ou "".
 * Estes dois predicados são a fronteira, e ficam aqui porque só o funil
 * pergunta "isto foi informado?" — o motor pergunta "quanto vale isto?", que
 * é outra coisa e já tem resposta no core. */

const temTexto = (valor: unknown): boolean =>
  typeof valor === 'string' && valor.trim() !== '';

/** Informado E diferente de zero. Zero digitado é resposta; zero ausente não. */
function temNumero(valor: unknown): boolean {
  if (typeof valor === 'number') return Number.isFinite(valor) && valor !== 0;
  if (typeof valor === 'string' && valor.trim() !== '') {
    const n = Number(valor);
    return Number.isFinite(n) && n !== 0;
  }
  return false;
}

export const ETAPAS = [
  {
    id: 'empresa',
    rotulo: 'Empresa',
    nome: 'Empresa',
    rota: '/empresa',
    grupo: 'configuracao',
    casco: 'formulario',
    resumo: 'Quem é a empresa e como ela é tributada.',
    requer: [],
    preenchida: ({ integrador }) => temTexto(integrador?.nome_empresa),
  },
  {
    id: 'custos',
    rotulo: 'Custos',
    nome: 'Estrutura de custos',
    rota: '/custos',
    grupo: 'configuracao',
    casco: 'formulario',
    resumo: 'O que entra no custo de cada projeto.',
    requer: ['empresa'],
    // Uma configuração de custo qualquer já resolve a etapa: quem só instala e
    // não projeta tem UMA linha preenchida, e cobrar as seis faria a trilha
    // acusar pendência que não existe.
    preenchida: ({ integrador }) => {
      const campos = Object.values(integrador?.configs?.campos ?? {});
      const extras = integrador?.configs?.extras ?? [];
      return (
        campos.some((cfg) => cfg?.modo != null) ||
        extras.some((extra) => temTexto(extra?.label) && extra?.modo != null)
      );
    },
  },
  {
    id: 'despesas',
    rotulo: 'Despesas',
    nome: 'Despesas fixas e variáveis',
    rota: '/despesas',
    grupo: 'configuracao',
    casco: 'formulario',
    resumo: 'O que a empresa paga todo mês, independentemente de vender.',
    requer: ['empresa'],
    preenchida: ({ integrador }) => {
      const d = integrador?.despesas;
      return (
        temNumero(d?.comissao?.valor) ||
        temNumero(d?.tributo?.valor) ||
        temNumero(d?.outras) ||
        temNumero(d?.despFixa) ||
        (d?.extras ?? []).some((extra) => temTexto(extra?.nome) && temNumero(extra?.valor))
      );
    },
  },
  {
    id: 'sazonalidade',
    rotulo: 'Sazonalidade',
    nome: 'Sazonalidade',
    rota: '/sazonalidade',
    grupo: 'configuracao',
    casco: 'formulario',
    resumo: 'Como o volume se distribui ao longo do ano.',
    requer: ['empresa'],
    // A etapa pede o histórico mês a mês. `projetos_mes` continua contando
    // porque é a média que a tela oferecia antes: quem já respondeu não vai
    // ser mandado responder de novo por uma mudança de formulário.
    preenchida: ({ integrador }) =>
      temNumero(integrador?.projetos_mes) ||
      Object.values(integrador?.sazonalidade?.meses ?? {}).some((mes) =>
        temNumero(mes?.projetos),
      ),
  },
  {
    id: 'indicadores',
    rotulo: 'Indicadores',
    nome: 'Indicadores',
    rota: '/indicadores',
    grupo: 'configuracao',
    casco: 'formulario',
    resumo: 'Volume, ticket e a margem líquida que se quer atingir.',
    requer: ['empresa'],
    // É a tela de CONFERÊNCIA da configuração: ela fecha quando existe meta
    // escolhida e existe volume, que são as duas entradas sem as quais o motor
    // se recusa a calcular preço.
    preenchida: ({ integrador }) =>
      temNumero(integrador?.lucro_alvo) &&
      (temNumero(integrador?.projetos_mes) ||
        Object.values(integrador?.sazonalidade?.meses ?? {}).some((mes) =>
          temNumero(mes?.projetos),
        )),
  },
  {
    id: 'projeto',
    rotulo: 'Projeto',
    nome: 'Dados do projeto',
    rota: '/projeto',
    grupo: 'precificacao',
    casco: 'formulario',
    resumo: 'O projeto que está sendo orçado agora.',
    requer: ['empresa'],
    // Preço de venda é o que a etapa existe para capturar: sem ele a
    // recomendação responde 'sem-dados' e a tela de preço não tem o que dizer.
    preenchida: ({ projeto }) => temNumero(projeto?.valor_venda),
  },
  {
    id: 'preco',
    rotulo: 'Preço',
    nome: 'Preço sugerido',
    rota: '/',
    grupo: 'precificacao',
    casco: 'painel',
    resumo: 'O piso de preço que entrega a margem alvo.',
    requer: ['empresa'],
    // O destino do funil não é um formulário: ele não "fica preenchido", ele
    // fica ALCANÇÁVEL. Quem responde por ele é a etapa de projeto.
    preenchida: ({ projeto }) => temNumero(projeto?.valor_venda),
  },
] as const satisfies readonly Etapa[];

export type IdEtapa = (typeof ETAPAS)[number]['id'];

/** Telas que existem fora do funil (consulta, não configuração). */
export const FORA_DO_FUNIL = [
  { id: 'relatorio', nome: 'Relatório', rota: '/relatorio' },
  { id: 'historico', nome: 'Histórico', rota: '/historico' },
] as const;

/** A navegação lateral, derivada da MESMA lista. Sem menu escrito à mão. */
export const GRUPOS_NAVEGACAO = [
  {
    titulo: 'Configuração',
    itens: ETAPAS.filter((e) => e.grupo === 'configuracao').map((e) => ({
      id: e.id as string,
      nome: e.nome as string,
      rota: e.rota as string,
    })),
  },
  {
    titulo: 'Precificação',
    itens: ETAPAS.filter((e) => e.grupo === 'precificacao').map((e) => ({
      id: e.id as string,
      nome: e.nome as string,
      rota: e.rota as string,
    })),
  },
  {
    titulo: 'Consulta',
    itens: FORA_DO_FUNIL.map((e) => ({ id: e.id as string, nome: e.nome as string, rota: e.rota as string })),
  },
] as const;

/** Posição (base 1) da etapa no funil; 0 quando a rota não é do funil. */
export function posicaoDaEtapa(id: string): number {
  return ETAPAS.findIndex((e) => e.id === id) + 1;
}

/** A etapa cuja rota corresponde ao caminho atual, se houver. */
export function etapaDaRota(caminho: string): Etapa | undefined {
  return ETAPAS.find((e) => e.rota === caminho);
}

/** A etapa seguinte no fluxo. `undefined` no fim do funil. */
export function proximaEtapa(id: string): Etapa | undefined {
  const i = ETAPAS.findIndex((e) => e.id === id);
  return i === -1 ? undefined : ETAPAS[i + 1];
}

/** A etapa anterior no fluxo. `undefined` no começo do funil. */
export function etapaAnterior(id: string): Etapa | undefined {
  const i = ETAPAS.findIndex((e) => e.id === id);
  return i <= 0 ? undefined : ETAPAS[i - 1];
}

/* ── O estado da trilha ────────────────────────────────────────────────────
 * Uma etapa tem TRÊS informações independentes, e confundi-las é o que faz um
 * stepper enganar:
 *
 *   resolvida  o dado existe no banco. Não tem nada a ver com onde a pessoa
 *              está agora, e é o que responde "o que ainda falta de mim".
 *   atual      é a tela aberta.
 *   aberta     dá para navegar até lá. Depende de `requer`, não da ordem.
 *
 * Uma etapa pode estar resolvida e não ser a atual, pode ser a atual e não
 * estar resolvida, e pode estar aberta sem ser nenhuma das duas. */

export type EstadoEtapa = Etapa & {
  /** Posição base 1 no funil. */
  posicao: number;
  resolvida: boolean;
  atual: boolean;
  aberta: boolean;
};

export type ProgressoFunil = {
  etapas: readonly EstadoEtapa[];
  /** Quantas etapas de CONFIGURAÇÃO já estão resolvidas. */
  resolvidas: number;
  /** Quantas etapas de configuração existem. */
  total: number;
  /** A primeira etapa não resolvida: para onde o "continuar de onde parei" leva. */
  pendente: EstadoEtapa | undefined;
};

/**
 * Monta o estado da trilha inteira.
 *
 * Função pura, sem React e sem fetch: recebe os dados e devolve o retrato. O
 * hook que busca no servidor mora em `src/dados/use-progresso.ts` e só chama
 * isto — assim a regra do funil continua testável sem montar componente.
 */
export function progressoDoFunil(dados: DadosDoFunil, atual?: string): ProgressoFunil {
  const resolvidas = new Set(ETAPAS.filter((e) => e.preenchida(dados)).map((e) => e.id as string));

  const etapas = ETAPAS.map((etapa, i): EstadoEtapa => ({
    ...etapa,
    posicao: i + 1,
    resolvida: resolvidas.has(etapa.id),
    atual: etapa.id === atual,
    aberta: etapa.requer.every((id) => resolvidas.has(id)),
  }));

  // Só a configuração conta no "x de y": as duas etapas de precificação são o
  // trabalho do dia, e um contador que nunca chega ao fim não mede nada.
  const configuracao = etapas.filter((e) => e.grupo === 'configuracao');

  return {
    etapas,
    resolvidas: configuracao.filter((e) => e.resolvida).length,
    total: configuracao.length,
    pendente: etapas.find((e) => !e.resolvida),
  };
}
